// Staff Availability → Day schedule. One column per stylist and an "Any stylist" column, so the desk
// sees the whole day at once: who is free, who is booked, who is off, how many any-stylist places
// are left, and which bookings still need a stylist. Click a free slot to book it, a booking to
// change its stylist or time, and a waiting booking to assign it.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, formatPhone, isoDate } from '../services/api';
import AssignStylistModal from '../components/AssignStylistModal';
import BookingModal from '../components/BookingModal';
import { AppointmentStatus, DetailRow, EmptyState, Loader, Modal, SourceBadge } from '../components/ui';

function dayTitle(iso) {
  const today = isoDate();
  const label = new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  if (iso === today) return `Today · ${label}`;
  if (iso === addDays(today, 1)) return `Tomorrow · ${label}`;
  return label;
}

function BookedCell({ appt, onOpen }) {
  return (
    <button type="button" className={`nye-cell-card nye-cell-card--${appt.status}`} onClick={() => onOpen(appt)} title="Open this booking">
      <span className="nye-cell-name">{appt.customer_name || formatPhone(appt.customer_phone)}</span>
      <span className="nye-cell-sub">{appt.service_name}</span>
    </button>
  );
}

function AnyCell({ row, onBook, onAssign }) {
  if (row.past && !row.any.waiting.length) return <span className="nye-cell-dash">—</span>;
  const { left, waiting } = row.any;
  return (
    <div className="nye-any-cell">
      {row.past ? null : left > 0 ? (
        <button type="button" className="nye-any-free" onClick={onBook} title="Book an any-stylist appointment at this time">
          {left} place{left > 1 ? 's' : ''} free
        </button>
      ) : left === 0 ? (
        <span className="nye-any-full">Full</span>
      ) : (
        <span className="nye-any-over" title="More any-stylist bookings than free stylists at this time">
          Over by {-left}
        </span>
      )}
      {waiting.map((w) => (
        <button key={w.id} type="button" className="nye-wait-chip" onClick={() => onAssign(w)} title="Waiting for a stylist. Click to assign.">
          ⏳ {w.customer_name || formatPhone(w.customer_phone)} · {w.service_name}
        </button>
      ))}
    </div>
  );
}

export default function ScheduleBoard() {
  const [day, setDay] = useState(isoDate());
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null); // preset for BookingModal
  const [assignFor, setAssignFor] = useState(null); // appointment id
  const [detail, setDetail] = useState(null); // appointment brief

  const load = useCallback(
    (silent) => {
      if (!silent) setLoading(true);
      return api
        .scheduleDay(day)
        .then(setBoard)
        .catch((e) => toast.error(e.message || 'Could not load the schedule'))
        .finally(() => setLoading(false));
    },
    [day],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Keep "now" and new WhatsApp bookings fresh while the desk has the board open.
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const s = board?.summary;
  const stylists = board?.stylists || [];

  return (
    <div className="nye-fade">
      <div className="nye-board-bar">
        <div className="nye-row" style={{ gap: 6, alignItems: 'center' }}>
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setDay(addDays(day, -1))} aria-label="Previous day">
            ‹
          </button>
          <input type="date" className="nye-input nye-input--bordered" style={{ width: 160, padding: '7px 10px' }} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setDay(addDays(day, 1))} aria-label="Next day">
            ›
          </button>
          {day !== isoDate() ? (
            <button type="button" className="nye-chip" onClick={() => setDay(isoDate())}>
              Today
            </button>
          ) : null}
        </div>
        <div className="nye-strong" style={{ flex: 1, minWidth: 200, fontSize: 15 }}>
          {dayTitle(day)}
        </div>
        <div className="nye-row" style={{ gap: 8 }}>
          <button type="button" className="nye-btn nye-btn--primary nye-btn--sm" onClick={() => setBooking({ mode: 'walk_in' })}>
            🚶 Walk-in
          </button>
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setBooking({ mode: 'later', date: day })}>
            + Book
          </button>
        </div>
      </div>

      {s ? (
        <div className="nye-board-stats">
          <span>
            <b>{s.working}</b> stylist{s.working === 1 ? '' : 's'} working
          </span>
          <span className="ok">
            <b>{s.free}</b> free
          </span>
          <span className="booked">
            <b>{s.booked}</b> booked
          </span>
          <span className="off">
            <b>{s.off}</b> off
          </span>
          <span className={s.waiting ? 'wait' : ''}>
            <b>{s.waiting}</b> waiting for a stylist
          </span>
        </div>
      ) : null}

      {board?.waiting?.length ? (
        <div className="nye-waiting-card">
          <div className="nye-strong" style={{ marginBottom: 8 }}>
            ⏳ {board.waiting.length} booking{board.waiting.length > 1 ? 's' : ''} waiting for a stylist
          </div>
          {board.waiting.map((w) => (
            <div key={w.id} className="nye-waiting-row">
              <span className="nye-mono" style={{ minWidth: 70 }}>
                {w.time_label}
              </span>
              <span className="nye-strong" style={{ minWidth: 120 }}>
                {w.customer_name || formatPhone(w.customer_phone)}
              </span>
              <span className="nye-muted" style={{ flex: 1 }}>
                {w.service_name}
              </span>
              <SourceBadge source={w.source} />
              <button type="button" className="nye-btn nye-btn--gold nye-btn--xs" onClick={() => setAssignFor(w.id)}>
                Assign stylist
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {s?.overbooked_times?.length ? (
        <div className="nye-callout nye-callout--danger nye-mt-8">
          More any-stylist bookings than free stylists at {s.overbooked_times.join(', ')}. Assign or move one of them, or free up a stylist.
        </div>
      ) : null}

      {loading && !board ? (
        <Loader />
      ) : !stylists.length ? (
        <div className="nye-card nye-mt-16">
          <EmptyState icon="💇" title="No stylists yet" body="Add stylists in the Stylists tab, then generate their weekly slots." />
        </div>
      ) : !board.rows.length ? (
        <div className="nye-card nye-mt-16">
          <EmptyState icon="🗓" title="Nobody is on the schedule this day" body="Generate weekly slots in the Stylists tab, or pick another day." />
        </div>
      ) : (
        <div className="nye-board-wrap nye-mt-16">
          <table className="nye-board">
            <thead>
              <tr>
                <th className="nye-board-time">Time</th>
                {stylists.map((st) => (
                  <th key={st.id}>
                    <div>{st.name}</div>
                    <div className="nye-board-sub">{st.working ? `${st.hours} · ${st.free} free` : 'Not working'}</div>
                  </th>
                ))}
                <th className="nye-board-any">
                  <div>Any stylist</div>
                  <div className="nye-board-sub">places left · waiting</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {board.rows.map((r) => (
                <tr key={r.time} className={r.past ? 'past' : ''}>
                  <td className="nye-board-time">{r.label}</td>
                  {stylists.map((st) => {
                    const c = r.cells[String(st.id)];
                    if (!c) return <td key={st.id} className="nye-cell nye-cell--none" />;
                    if (c.state === 'cont') return <td key={st.id} className="nye-cell nye-cell--cont" title="Part of a longer slot" />;
                    if (c.state === 'booked')
                      return (
                        <td key={st.id} className="nye-cell nye-cell--booked">
                          {c.appointment ? <BookedCell appt={c.appointment} onOpen={setDetail} /> : <span className="nye-cell-dash">Booked</span>}
                        </td>
                      );
                    if (c.state === 'off')
                      return (
                        <td key={st.id} className="nye-cell nye-cell--off">
                          Off
                        </td>
                      );
                    if (c.state === 'past') return <td key={st.id} className="nye-cell nye-cell--past" />;
                    return (
                      <td key={st.id} className="nye-cell nye-cell--free">
                        <button
                          type="button"
                          className="nye-free-btn"
                          onClick={() => setBooking({ mode: 'later', date: day, staffId: st.id, slotId: c.slot_id, time: r.time })}
                          title={`Book ${st.name} at ${r.label}`}
                        >
                          <span>Free</span>
                          <span className="nye-free-plus">+ Book</span>
                        </button>
                      </td>
                    );
                  })}
                  <td className="nye-cell nye-cell--any">
                    <AnyCell
                      row={r}
                      onBook={() => setBooking({ mode: 'later', date: day, any: true, time: r.time })}
                      onAssign={(w) => setAssignFor(w.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="nye-board-legend">
        <span>
          <i className="free" /> Free (click to book)
        </span>
        <span>
          <i className="booked" /> Booked
        </span>
        <span>
          <i className="off" /> Marked off
        </span>
        <span>
          <i className="wait" /> Waiting for a stylist
        </span>
        <span className="nye-muted">Any stylist = stylists free at that time minus bookings already waiting.</span>
      </div>

      {booking ? <BookingModal preset={booking} onClose={() => setBooking(null)} onBooked={() => load(true)} /> : null}
      {assignFor ? (
        <AssignStylistModal
          appointmentId={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => {
            setDetail(null);
            load(true);
          }}
        />
      ) : null}
      {detail ? (
        <Modal title="Booking" onClose={() => setDetail(null)}>
          <DetailRow label="Booked for">{detail.customer_name || '—'}</DetailRow>
          {detail.contact_name && detail.contact_name !== detail.customer_name ? <DetailRow label="Contact">{detail.contact_name}</DetailRow> : null}
          <DetailRow label="Phone">{formatPhone(detail.customer_phone)}</DetailRow>
          <DetailRow label="Service">{detail.service_name}</DetailRow>
          <DetailRow label="Time">{detail.time_label}</DetailRow>
          <DetailRow label="Stylist">{detail.staff_name || 'Any available stylist'}</DetailRow>
          <DetailRow label="Status">
            <AppointmentStatus status={detail.status} />
          </DetailRow>
          <DetailRow label="Source">
            <SourceBadge source={detail.source} showLabel />
          </DetailRow>
          <div className="nye-row nye-mt-16" style={{ gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="nye-btn nye-btn--outline" onClick={() => setDetail(null)}>
              Close
            </button>
            {detail.status === 'booked' ? (
              <button type="button" className="nye-btn nye-btn--primary" onClick={() => setAssignFor(detail.id)}>
                Change stylist or time
              </button>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
